import React, { useEffect } from "react";
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
    useLocation,
    useNavigate,
} from "react-router-dom";
import HomePage from "../pages/HomePage/HomePage";
import AccountPage from "../pages/AccountPage/AccountPage";
import PalletVisualizerPage from "../pages/PalletVisualizerPage/PalletVisualizerPage";
import ManifestPage from "../pages/ManifestPage/ManifestPage";
import CatalogPage from "../pages/CatalogPage/CatalogPage";
import ProtectedRoute from "../pages/ProtectedRoute";
import LoginPage from "../pages/auth/LoginPage";
import SignUpPage from "../pages/auth/SignUpPage";
import ForgotPasswordPage from "../pages/auth/ForgotPasswordPage";
import CheckEmailPage from "../pages/auth/CheckEmailPage";
import VerifyPage from "../pages/auth/VerifyPage";
import ResetPasswordPage from "../pages/auth/ResetPasswordPage";
import MagicLoginPage from "../pages/auth/MagicLoginPage";
import AccountSuspendedPage from "../pages/auth/AccountSuspendedPage";
import AdminProtectedRoute from "../pages/admin/AdminProtectedRoute";
import AdminDashboardPage from "../pages/admin/AdminDashboardPage";
import AdminUsersPage from "../pages/admin/AdminUsersPage";
import AdminUserDetailPage from "../pages/admin/AdminUserDetailPage";
import AdminAuditLogPage from "../pages/admin/AdminAuditLogPage";
import AdminPackDetailPage from "../pages/admin/AdminPackDetailPage";
import ForbiddenPage from "../pages/admin/ForbiddenPage";
import LeftMenu from "../components/LeftMenu/LeftMenu";
import FeedbackWidget from "../components/FeedbackWidget/FeedbackWidget";
import DashboardPage from "../pages/DashboardPage/DashboardPage";
import { AuthProvider, useAuth } from "../services/AuthContext";
import "./App.css";

function NavigationGuard() {
    const { suspended } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    useEffect(() => {
        if (suspended && location.pathname !== "/account-suspended") {
            navigate("/account-suspended", { replace: true });
        }
    }, [suspended, location.pathname, navigate]);
    return null;
}

function AppShell() {
    const { user, loading, logout, suspended } = useAuth();

    if (loading) {
        return <div className="app-loading">Initializing…</div>;
    }

    return (
        <div className="app-shell">
            <NavigationGuard />
            {user && !suspended && <LeftMenu user={user} onSignOut={logout} />}
            <main className="app-main">
                <div className="app-route">
                    <Routes>
                        <Route
                            path="/"
                            element={
                                user ? (
                                    <HomePage user={user} />
                                ) : (
                                    <Navigate to="/login" replace />
                                )
                            }
                        />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/sign-up" element={<SignUpPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/auth/check-email" element={<CheckEmailPage />} />
                        <Route path="/auth/verify" element={<VerifyPage />} />
                        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/auth/magic-login" element={<MagicLoginPage />} />
                        <Route path="/account-suspended" element={<AccountSuspendedPage />} />
                        <Route path="/forbidden" element={<ForbiddenPage />} />
                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute>
                                    <DashboardPage user={user} />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/account"
                            element={
                                <ProtectedRoute>
                                    <AccountPage user={user} onSignOut={logout} />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/visualizer"
                            element={
                                <ProtectedRoute>
                                    <PalletVisualizerPage user={user} />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/manifest"
                            element={
                                <ProtectedRoute>
                                    <ManifestPage user={user} />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/catalog"
                            element={
                                <ProtectedRoute>
                                    <CatalogPage user={user} />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin"
                            element={
                                <AdminProtectedRoute>
                                    <AdminDashboardPage />
                                </AdminProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/users"
                            element={
                                <AdminProtectedRoute>
                                    <AdminUsersPage />
                                </AdminProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/users/:id"
                            element={
                                <AdminProtectedRoute>
                                    <AdminUserDetailPage />
                                </AdminProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/events"
                            element={
                                <AdminProtectedRoute>
                                    <AdminAuditLogPage />
                                </AdminProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/packs/:id"
                            element={
                                <AdminProtectedRoute>
                                    <AdminPackDetailPage />
                                </AdminProtectedRoute>
                            }
                        />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </div>
            </main>
            <FeedbackWidget />
        </div>
    );
}

function App() {
    return (
        <Router>
            <AuthProvider>
                <AppShell />
            </AuthProvider>
        </Router>
    );
}

export default App;
